import { Node } from '@xyflow/react';
import {
  ReviewProfileCheckpoint,
  ReviewProfilePolicy,
  ReviewProfileSource,
  ReviewProfileVerdict,
} from '../../../api/structs/reviewProfile';
import { PipelineCanvasEdge } from '../canvas/graph/types';

// ==================== Node ids ====================
// `rv:` namespaced so a reviewer graph and a pipeline graph could share one
// React Flow instance without an id ever colliding.

export const REVIEWER_CORE_NODE_ID = 'rv:core';
export const REVIEWER_VERDICT_NODE_ID = 'rv:verdict';
export const reviewSourceNodeId = (id: string): string => `rv:src:${id}`;
export const reviewPolicyNodeId = (id: string): string => `rv:pol:${id}`;

// ==================== Handle ids ====================
// Every judge-template slot is a PORT on the core node, and a source wires to
// the slot it fills — that is the whole point of the picture, so the handle
// scheme is slot-shaped rather than node-shaped.

/** A source node's single outgoing pin; one source may feed several slots. */
export const REVIEW_SOURCE_HANDLE = 'rv:out';
/** `{{slot}}` port on the core. */
export const reviewSlotHandle = (name: string): string => `rv:slot:${name}`;
/**
 * The core's catch-all port. A source that fills no NAMED slot (the candidate
 * itself, the rendered clip, the reference descriptions that ride along with
 * the reference labels) still reaches the judge — it just does not arrive
 * through a `{{placeholder}}`.
 */
export const REVIEW_CORE_ANY_SLOT_HANDLE = 'rv:slot';
/** The core's ruling, out to the verdict fork. */
export const REVIEW_CORE_RULING_HANDLE = 'rv:ruling';
/** The verdict node's inbound pin, and each policy node's. */
export const REVIEW_IN_HANDLE = 'rv:in';
/** One fork of the verdict: `rv:v:pass` / `rv:v:uncertain` / `rv:v:fail`. */
export const reviewVerdictHandle = (verdict: string): string => `rv:v:${verdict}`;

// ==================== Edge kinds ====================

export type ReviewerEdgeKind =
  | 'review_text'
  | 'review_image'
  | 'review_check'
  | 'review_pass'
  | 'review_uncertain'
  | 'review_fail';

/** The three forks of a verdict, in the order the fork node lists them. */
export const REVIEW_VERDICT_OUTCOMES: ReviewProfileVerdict[] = ['pass', 'uncertain', 'fail'];

export const reviewVerdictEdgeKind = (verdict: string): ReviewerEdgeKind => {
  if (verdict === 'pass') return 'review_pass';
  if (verdict === 'uncertain') return 'review_uncertain';
  return 'review_fail';
};

// ==================== Node data ====================

export type ReviewerNodeType =
  | 'review-source'
  | 'reviewer-core'
  | 'review-policy'
  | 'review-verdict';

/**
 * `on` = built and switched on, `off` = built and switched off, `unavailable` =
 * not implemented yet. The three read differently on purpose: "off" is an
 * invitation, "unavailable" is not.
 */
export type ReviewerStatus = 'on' | 'off' | 'unavailable';

/** `fired`/`skipped` from a run's own record; absent when the run says nothing. */
export type ReviewPolicyOutcome = 'fired' | 'skipped';

/** Which source row a node sits in: what the judge READS vs what it SEES. */
export type ReviewerSourceLane = 'text' | 'image';

export interface ReviewSourceNodeData {
  kind: 'review-source';
  source: ReviewProfileSource;
  status: ReviewerStatus;
  lane: ReviewerSourceLane;
  /** The kind of the edge this source draws — also its colour. */
  edgeKind: ReviewerEdgeKind;
  [key: string]: unknown;
}

/** One `{{placeholder}}` the judge template asks for, as a port on the core. */
export interface ReviewerSlotPort {
  /** `rv:slot:<name>`. */
  handle: string;
  name: string;
  /** The source id that fills it. */
  source: string;
  filled: boolean;
  /** True when ONLY the frame-pair judge asks for this slot. */
  pairOnly: boolean;
  note?: string;
}

export interface ReviewerCoreNodeData {
  kind: 'reviewer-core';
  checkpoint: ReviewProfileCheckpoint;
  /** The resolved review mode (`off` / `on` / `strict`). */
  mode: string;
  /** Still slots in template order, then pair-only slots. */
  ports: ReviewerSlotPort[];
  /** Last outcome per tier from a run's traces; empty without run evidence. */
  tierOutcomes: Record<string, string>;
  [key: string]: unknown;
}

export interface ReviewPolicyNodeData {
  kind: 'review-policy';
  policy: ReviewProfilePolicy;
  status: ReviewerStatus;
  outcome?: ReviewPolicyOutcome;
  [key: string]: unknown;
}

export interface ReviewVerdictNodeData {
  kind: 'review-verdict';
  verdicts: ReviewProfileVerdict[];
  [key: string]: unknown;
}

export type ReviewerNodeData =
  | ReviewSourceNodeData
  | ReviewerCoreNodeData
  | ReviewPolicyNodeData
  | ReviewVerdictNodeData;

export type ReviewerNode = Node<ReviewerNodeData>;
/** Reviewer edges are pipeline-canvas edges: same styling and rendering path. */
export type ReviewerEdge = PipelineCanvasEdge;

// ==================== Lanes ====================
// Fixed columns, left to right: what the judge reads (two rows — text above,
// images below) → the judge → its verdict → what it may then do. Positions are
// derived, never stored, so the picture cannot be dragged out of shape.

export type ReviewerLaneId = 'sourceText' | 'sourceImage' | 'core' | 'verdict' | 'policy';

export interface ReviewerLane {
  x: number;
  /** The lane's node width, so callers can prove the lanes do not overlap. */
  width: number;
  /** Vertical distance between two rows of this lane. */
  pitch: number;
}

export const REVIEWER_LANES: Record<ReviewerLaneId, ReviewerLane> = {
  sourceText: { x: 0, width: 190, pitch: 96 },
  sourceImage: { x: 0, width: 190, pitch: 96 },
  core: { x: 300, width: 260, pitch: 96 },
  verdict: { x: 660, width: 190, pitch: 96 },
  policy: { x: 900, width: 260, pitch: 96 },
};

/** Blank rows between the text source row and the image source row. */
export const REVIEWER_LANE_GAP = 48;
